import { useState, useCallback, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Stethoscope, Calculator, FlaskConical, BookOpen, GraduationCap, Loader2, Sparkles, AlertTriangle, Quote, BookMarked } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { thesisApi } from '@/api/thesisApi';
import DefenseInlineSuggestionsDialog from '@/components/DefenseInlineSuggestionsDialog';

const REVIEWERS = [
  { key: 'clinician',    label: 'Clinician',           icon: Stethoscope,    color: 'text-emerald-700 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-900 dark:text-emerald-300' },
  { key: 'statistician', label: 'Statistician',        icon: Calculator,     color: 'text-blue-700 bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-900 dark:text-blue-300' },
  { key: 'methodology',  label: 'Methodology',         icon: FlaskConical,   color: 'text-purple-700 bg-purple-50 border-purple-200 dark:bg-purple-950/30 dark:border-purple-900 dark:text-purple-300' },
  { key: 'journal',      label: 'Journal Reviewer',    icon: BookOpen,       color: 'text-amber-700 bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900 dark:text-amber-300' },
  { key: 'committee',    label: 'Committee Member',    icon: GraduationCap,  color: 'text-rose-700 bg-rose-50 border-rose-200 dark:bg-rose-950/30 dark:border-rose-900 dark:text-rose-300' },
];

/**
 * DefenseSimulationDialog — Sprint 3 (Step 11).
 *
 * Run a 5-AI-reviewer panel against the active chapter. Each reviewer returns
 * a structured JSON critique; the dialog renders them in a tabbed grid.
 */
export default function DefenseSimulationDialog({
  open, onOpenChange,
  userId, chapter, language = 'en',
  onChapterPatched,           // (newMarkdown) => void
}) {
  const [phase, setPhase] = useState('idle');
  const [report, setReport] = useState(null);
  const [activeReviewer, setActiveReviewer] = useState(REVIEWERS[0].key);
  const [defenseId, setDefenseId] = useState(null);
  const [inlineOpen, setInlineOpen] = useState(false);
  const pollRef = useRef(null);

  // Stop polling on unmount / close
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const poll = useCallback((id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const tick = async () => {
      try {
        const r = await thesisApi.getDefenseReport(userId, id);
        if (r.status === 'ready') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setReport(r);
          setPhase('done');
          toast.success(`Defense panel: ${r.stats?.total_concerns || 0} concerns across ${r.stats?.reviewers_succeeded || 5} reviewers`);
        } else if (r.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          toast.error(`Defense simulation failed: ${r.error || 'Unknown error'}`);
          setPhase('idle');
        }
      } catch (e) {
        // Transient; keep polling unless 404
        if (e?.response?.status === 404) {
          clearInterval(pollRef.current); pollRef.current = null;
          toast.error('Report not found');
          setPhase('idle');
        }
      }
    };
    pollRef.current = setInterval(tick, 5000);
    tick();
  }, [userId]);

  const run = useCallback(async () => {
    if (!chapter?.id) return;
    setPhase('running');
    setReport(null);
    try {
      const res = await thesisApi.simulateDefense(userId, chapter.id, null, language);
      setDefenseId(res.defense_id);
      poll(res.defense_id);
    } catch (e) {
      toast.error(`Defense simulation failed to start: ${e?.response?.data?.detail || e.message}`);
      setPhase('idle');
    }
  }, [userId, chapter, language, poll]);

  const current = report?.reviewers?.find(r => r.perspective === activeReviewer);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (phase !== 'running') onOpenChange(v); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="defense-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-serif">
            <GraduationCap className="h-5 w-5 text-rose-600" />
            Simulate Dissertation Defense
          </DialogTitle>
          <DialogDescription>
            Five AI reviewer perspectives critique your chapter as a doctoral committee would. Use the results to anticipate questions and strengthen weak areas before submission.
          </DialogDescription>
        </DialogHeader>

        {phase === 'idle' && (
          <div className="space-y-4">
            <div className="rounded-lg border border-rose-200 bg-rose-50/60 dark:bg-rose-950/20 dark:border-rose-900 p-4">
              <p className="text-sm text-slate-800 dark:text-slate-200">
                <strong>Chapter:</strong> {chapter?.title || '—'} · {((chapter?.markdown || '').split(/\s+/).length).toLocaleString()} words
              </p>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                Reviewers run in parallel and typically take 30–90 seconds. Each returns specific concerns, suggested improvements, and a defense-response template.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {REVIEWERS.map(r => {
                const Icon = r.icon;
                return (
                  <div key={r.key} className={`rounded-lg border px-3 py-3 text-center ${r.color}`}>
                    <Icon className="h-5 w-5 mx-auto mb-1" />
                    <p className="text-xs font-semibold">{r.label}</p>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="defense-cancel">Cancel</Button>
              <Button onClick={run} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="defense-run-btn">
                <Sparkles className="h-4 w-4 mr-2" /> Run Defense Panel
              </Button>
            </div>
          </div>
        )}

        {phase === 'running' && (
          <div className="py-16 text-center">
            <Loader2 className="h-10 w-10 mx-auto animate-spin text-rose-600" />
            <p className="mt-4 text-sm font-medium">Five reviewers analyzing your chapter…</p>
            <p className="mt-1 text-xs text-slate-500">Parallel processing — typically 60–140 seconds. We poll every 5 seconds; this dialog will update automatically.</p>
            {defenseId && <p className="mt-2 text-[10px] font-mono text-slate-400">id: {defenseId}</p>}
          </div>
        )}

        {phase === 'done' && report && (
          <div className="space-y-4">
            {/* Overall summary */}
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 p-4">
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <Quote className="h-4 w-4 text-slate-500" /> Panel summary
              </h3>
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <ReactMarkdown>{report.overall_summary || ''}</ReactMarkdown>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-300">{report.stats?.total_concerns || 0} concerns</span>
                <span className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300">{report.stats?.unsupported_claims || 0} unsupported claims</span>
                <span className="px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300">{report.stats?.missing_literature || 0} missing-lit flags</span>
              </div>
            </div>

            {/* Reviewer tabs */}
            <div className="flex flex-wrap gap-1.5 border-b border-slate-200 dark:border-slate-700 pb-2">
              {REVIEWERS.map(r => {
                const Icon = r.icon;
                const isActive = r.key === activeReviewer;
                return (
                  <button key={r.key} type="button"
                    onClick={() => setActiveReviewer(r.key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition ${isActive ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                    data-testid={`defense-tab-${r.key}`}>
                    <Icon className="h-3.5 w-3.5" /> {r.label}
                  </button>
                );
              })}
            </div>

            {current && (
              <div className="space-y-3" data-testid={`defense-review-${current.perspective}`}>
                <div className="rounded-lg border border-slate-300 dark:border-slate-700 px-4 py-3">
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Headline concern</p>
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{current.headline_concern || '—'}</p>
                </div>

                {current.likely_criticisms?.length > 0 && (
                  <ReviewerSection title="Likely criticisms" icon={AlertTriangle} color="amber" items={current.likely_criticisms} />
                )}
                {current.unsupported_claims?.length > 0 && (
                  <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50/40 dark:bg-red-950/20 p-3">
                    <p className="text-xs font-semibold text-red-700 dark:text-red-300 uppercase tracking-widest mb-2">Unsupported claims</p>
                    <ul className="space-y-2">
                      {current.unsupported_claims.map((c, i) => (
                        <li key={i} className="text-xs">
                          <p className="text-slate-800 dark:text-slate-200">"{c.claim}"</p>
                          {c.concern && <p className="text-slate-500 dark:text-slate-400 italic mt-0.5">{c.concern}</p>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {current.weak_areas?.length > 0 && (
                  <ReviewerSection title="Weak areas" icon={AlertTriangle} color="slate" items={current.weak_areas} />
                )}
                {current.methodological_concerns?.length > 0 && (
                  <ReviewerSection title="Methodological concerns" icon={FlaskConical} color="purple" items={current.methodological_concerns} />
                )}
                {current.missing_literature?.length > 0 && (
                  <div className="rounded-lg border border-blue-200 dark:border-blue-900 bg-blue-50/40 dark:bg-blue-950/20 p-3">
                    <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-widest mb-2 flex items-center gap-1">
                      <BookMarked className="h-3 w-3" /> Missing literature
                    </p>
                    <ul className="space-y-1.5">
                      {current.missing_literature.map((m, i) => (
                        <li key={i} className="text-xs">
                          <strong className="text-slate-800 dark:text-slate-200">{m.topic}</strong>
                          {m.why && <span className="text-slate-500 dark:text-slate-400"> — {m.why}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {current.suggested_defense_response && (
                  <div className="rounded-lg border border-emerald-200 dark:border-emerald-900 bg-emerald-50/40 dark:bg-emerald-950/20 p-3">
                    <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 uppercase tracking-widest mb-2">Suggested defense response</p>
                    <p className="text-sm text-slate-800 dark:text-slate-200 italic leading-relaxed">"{current.suggested_defense_response}"</p>
                  </div>
                )}
                {current.error && (
                  <p className="text-xs text-red-600 italic">⚠ This reviewer failed: {current.error}</p>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 dark:border-slate-700">
              <Button variant="ghost" onClick={() => setPhase('idle')} data-testid="defense-rerun">Run again</Button>
              {report && chapter?.id && (
                <Button
                  onClick={() => setInlineOpen(true)}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="defense-apply-inline-btn">
                  <Sparkles className="h-4 w-4 mr-1" /> Apply suggestions inline
                </Button>
              )}
              <Button onClick={() => onOpenChange(false)} data-testid="defense-close">Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
      {/* Inline suggestions layer (Sprint 7) — Grammarly-style accept/reject */}
      {report && chapter?.id && (
        <DefenseInlineSuggestionsDialog
          open={inlineOpen}
          onOpenChange={setInlineOpen}
          userId={userId}
          defenseId={defenseId || report.id}
          chapterId={chapter.id}
          language={language}
          onChapterPatched={onChapterPatched}
        />
      )}
    </Dialog>
  );
}

function ReviewerSection({ title, icon: Icon, color, items }) {
  const colorMap = {
    amber: 'border-amber-200 bg-amber-50/40 dark:border-amber-900 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300',
    slate: 'border-slate-200 bg-slate-50/60 dark:border-slate-700 dark:bg-slate-900/30 text-slate-600 dark:text-slate-300',
    purple: 'border-purple-200 bg-purple-50/40 dark:border-purple-900 dark:bg-purple-950/20 text-purple-700 dark:text-purple-300',
  };
  return (
    <div className={`rounded-lg border p-3 ${colorMap[color]}`}>
      <p className="text-xs font-semibold uppercase tracking-widest mb-2 flex items-center gap-1">
        <Icon className="h-3 w-3" /> {title}
      </p>
      <ul className="space-y-1 list-disc list-inside text-xs text-slate-800 dark:text-slate-200">
        {items.map((c, i) => <li key={i}>{c}</li>)}
      </ul>
    </div>
  );
}
